import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Row,
  Grid,
  Card,
  Button,
  ViewContext,
  Loader,
  useAPI,
} from "components/lib";
import { useParams } from "react-router-dom";
import Axios from "axios";
import XpmSearchModal from "./components/XpmSearchModal";
import ConfirmDialog from "./components/ConfirmDialog";
import { readBlobErrorMessage } from "./pdf-error";

// Longer than the server's own CreditorWatch budget (8s login + 20s download),
// so a real upstream failure surfaces as our 502 rather than a client abort.
const PDF_REQUEST_TIMEOUT_MS = 60000;

function pollIntervalMs(elapsed) {
  return elapsed > 60000 ? 15000 : 5000;
}

function StatusBadge({ status }) {
  const cls =
    status === "ready"
      ? "bg-sg-green-bg text-sg-green"
      : status === "failed"
      ? "bg-sg-red-bg text-sg-red"
      : "bg-sg-orange-bg text-sg-orange";
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
      ? Number(value)
      : Number.NaN;
  return Number.isFinite(numeric) ? numeric.toLocaleString() : String(value);
}

function formatDate(value) {
  if (!value) return "N/A";
  if (typeof value !== "string") return formatValue(value);
  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
  }
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return value.includes("T") ? date.toLocaleString() : date.toLocaleDateString();
  }
  return value;
}

function formatIdentifierType(value) {
  if (!value) return "N/A";
  return value.toUpperCase().replace(/^AU_/, "AU ");
}

function displayName(record) {
  if (!record) return "N/A";
  if (record.full_name) return record.full_name;
  if (record.fullName) return record.fullName;
  if (record.individual) return record.individual;
  if (typeof record.name === "string") return record.name;
  const first = record.name?.firstName || record.first_name || record.firstName || "";
  const middle = record.name?.middleName || record.middle_name || "";
  const last = record.name?.lastName || record.last_name || record.lastName || "";
  return [first, middle, last].filter(Boolean).join(" ") || "N/A";
}

function displayAddress(record) {
  if (!record) return "N/A";
  if (record.fullAddress) return record.fullAddress;
  if (typeof record.address === "string") return record.address;
  const address = record.address || {};
  return (
    [
      address.street || address.line1,
      address.line2,
      address.city || address.suburb,
      address.state,
      address.postcode,
      address.country,
    ]
      .filter(Boolean)
      .join(", ") || "N/A"
  );
}

function displayOrganisationIdentifier(company) {
  if (!company?.asicOrganisationNumber) return company?.abn || "N/A";
  return `${company.asicOrganisationNumberType || "ASIC"} ${company.asicOrganisationNumber}`;
}

function FieldGrid({ items, columns = "md:grid-cols-2 lg:grid-cols-3" }) {
  const visible = items.filter(
    (item) => item && item.label && item.value !== null && item.value !== undefined && item.value !== ""
  );

  if (!visible.length) {
    return <div className="text-sm text-slate-500">No details available.</div>;
  }

  return (
    <div className={`grid grid-cols-1 gap-4 ${columns}`}>
      {visible.map((item) => (
        <div key={item.label} className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {item.label}
          </div>
          <div className="mt-1 text-sm text-slate-900 break-words">
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function DataTable({ columns, rows, emptyText = "No records available." }) {
  if (!rows.length) {
    return <div className="text-sm text-slate-500">{emptyText}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.label}
                className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {rows.map((row, index) => (
            <tr key={row.id || row.key || index}>
              {columns.map((column) => (
                <td key={column.label} className="px-3 py-3 align-top text-slate-900">
                  {column.render ? column.render(row) : formatValue(row[column.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
  );
}

function CompanyCard({ company, titlePrefix = "" }) {
  const addresses = Array.isArray(company?.organisationAddresses)
    ? company.organisationAddresses
    : [];
  const officeholders = Array.isArray(company?.officeholders) ? company.officeholders : [];
  const shareholders = Array.isArray(company?.shareholders) ? company.shareholders : [];
  const shareCapitalGroup = Array.isArray(company?.shareCapitalGroup)
    ? company.shareCapitalGroup
    : [];

  return (
    <Card>
      <div className="flex flex-col gap-6">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {titlePrefix ? `${titlePrefix}: ` : ""}
            {company?.name || "Unnamed company"}
          </h3>
          <div className="mt-2">
            <FieldGrid
              items={[
                { label: "Identifier", value: displayOrganisationIdentifier(company) },
                { label: "ABN", value: company?.abn || "N/A" },
                { label: "Status", value: company?.status || "N/A" },
                { label: "Type", value: company?.type || "N/A" },
                { label: "Class", value: company?.class || "N/A" },
                { label: "Subclass", value: company?.subClass || "N/A" },
                { label: "Registration date", value: formatDate(company?.registrationDate) },
                { label: "Locality", value: company?.locality || "N/A" },
              ]}
            />
          </div>
        </div>

        <div>
          <div className="mb-3 text-sm font-semibold text-slate-900">Addresses</div>
          <DataTable
            columns={[
              { label: "Type", render: (row) => formatValue(row.type) },
              { label: "Address", render: (row) => formatValue(row.fullAddress || displayAddress(row)) },
              { label: "Start date", render: (row) => formatDate(row.startDate) },
            ]}
            rows={addresses}
            emptyText="No addresses returned."
          />
        </div>

        <div>
          <div className="mb-3 text-sm font-semibold text-slate-900">Officeholders</div>
          <DataTable
            columns={[
              { label: "Type", render: (row) => formatValue(row.type) },
              { label: "Name", render: (row) => displayName(row) },
              { label: "DOB", render: (row) => formatDate(row.dateOfBirth) },
              { label: "Start date", render: (row) => formatDate(row.startDate) },
              { label: "Address", render: (row) => displayAddress(row) },
            ]}
            rows={officeholders}
            emptyText="No officeholders returned."
          />
        </div>

        <div>
          <div className="mb-3 text-sm font-semibold text-slate-900">Shareholders</div>
          <DataTable
            columns={[
              { label: "Name", render: (row) => displayName(row) },
              { label: "Holder type", render: (row) => formatValue(row.type) },
              { label: "Share class", render: (row) => formatValue(row.share?.type) },
              { label: "Amount", render: (row) => formatNumber(row.share?.amount) },
              {
                label: "Beneficially owned",
                render: (row) => formatValue(row.share?.beneficiallyOwned),
              },
              { label: "Joint holding", render: (row) => formatValue(row.share?.jointHolding) },
              {
                label: "Identifier",
                render: (row) =>
                  row.asicOrganisationNumber
                    ? `ACN ${row.asicOrganisationNumber}`
                    : formatValue(row.documentNumber),
              },
              { label: "Address", render: (row) => displayAddress(row) },
            ]}
            rows={shareholders}
            emptyText="No shareholders returned."
          />
        </div>

        <div>
          <div className="mb-3 text-sm font-semibold text-slate-900">Share capital</div>
          <DataTable
            columns={[
              { label: "Class code", render: (row) => formatValue(row.classCode) },
              { label: "Class title", render: (row) => formatValue(row.classTitle) },
              { label: "Shares", render: (row) => formatNumber(row.numberOfShares) },
              { label: "Paid", render: (row) => formatNumber(row.totalAmountPaid) },
              {
                label: "Due and payable",
                render: (row) => formatNumber(row.totalAmountDueAndPayable),
              },
            ]}
            rows={shareCapitalGroup}
            emptyText="No share capital returned."
          />
        </div>
      </div>
    </Card>
  );
}

export function EntityOnboardingReportView() {
  const params = useParams();
  const viewContext = useContext(ViewContext);
  const [refreshTick, setRefreshTick] = useState(0);
  const reportAPI = useAPI(
    `/api/ubo-reports/${params.report_id}`,
    "get",
    refreshTick
  );
  const refresh = () => setRefreshTick((n) => n + 1);
  const startedAt = useRef(Date.now());
  const [actionLoading, setActionLoading] = useState({});
  const [editing, setEditing] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  // `loading` only paints a spinner — Button never disables the DOM node — so
  // re-entrancy is guarded here. Each click is a live CreditorWatch download.
  const pdfInFlight = useRef(false);

  // XPM sync UI state
  const [xpmModal, setXpmModal] = useState(null); // { mode: 'org' | 'person', personId?, personKey? }
  const [orgMergeTarget, setOrgMergeTarget] = useState(null); // selected XPM client {ContactId, Name, ...}
  const [orgPreview, setOrgPreview] = useState(null); // { available_company_fields, resolved, is_using_defaults }
  const [orgFieldPicks, setOrgFieldPicks] = useState({}); // { Name: true, Email: false, ... }
  const [orgFieldEdits, setOrgFieldEdits] = useState({}); // { Name: "...", Email: "...", ... } — editable values for merge
  const [orgIncludeUbos, setOrgIncludeUbos] = useState(true);
  const [orgSyncLoading, setOrgSyncLoading] = useState(false);
  // "Add new" editable preview — distinct from merge so the two flows don't fight over state.
  const [addNewPreview, setAddNewPreview] = useState(null); // { available_company_fields, is_using_defaults }
  const [addNewValues, setAddNewValues] = useState({}); // { Name: "...", Email: "...", ... }

  // Modal-driven confirmations (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, body, confirmText, destructive?, onConfirm }
  const closeConfirm = () => setConfirmDialog(null);
  const [personClientChoice, setPersonClientChoice] = useState(null); // { personId, personKey, target }

  const data = reportAPI?.data;
  const status = data?.status;
  const organisation = data?.organisation || null;
  const people = data?.beneficial_owners?.owners || data?.people || [];
  const linkedCompanies = data?.beneficial_owners?.linked_companies || [];
  const organisationAddresses = data?.organisation_addresses || [];
  const officeholders = data?.officeholders || [];
  const shareholders = data?.shareholders || [];
  const shareCapitalGroup = data?.share_capital_group || [];
  const internationalCompanies = data?.international_companies || [];
  const investigatedCompanies = data?.investigated_companies || [];
  const orgPmsLinks = data?.org_pms_links || data?.data?.org_pms_links || [];

  useEffect(() => {
    if (!status || status === "ready" || status === "failed") return;
    const elapsed = Date.now() - startedAt.current;
    const timer = setTimeout(refresh, pollIntervalMs(elapsed));
    return () => clearTimeout(timer);
  }, [status, refreshTick]);

  if (reportAPI.loading && !data) return <Loader />;
  if (!data) return <div>Report not found</div>;

  async function openPdf() {
    if (pdfInFlight.current) return;

    // Open the tab synchronously — Button calls action() directly inside
    // onClick, so the user-gesture window is still open here. Opening it after
    // the await instead gets swallowed by the pop-up blocker.
    const tab = window.open("", "_blank");
    if (!tab) {
      viewContext.notification.show(
        "Please allow pop-ups to open the report PDF",
        "error",
        true
      );
      return;
    }
    // The PDF is fetched live from CreditorWatch, so avoid a blank tab.
    tab.document.write("Loading report PDF…");

    let objectUrl;
    pdfInFlight.current = true;
    setPdfLoading(true);
    try {
      const res = await Axios.get(`/api/ubo-reports/${params.report_id}/pdf`, {
        responseType: "blob",
        timeout: PDF_REQUEST_TIMEOUT_MS,
      });
      objectUrl = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      tab.location = objectUrl;
      // Revoke late: unlike an anchor download, a navigation does not commit
      // synchronously, so revoking now would race the tab into a blank page.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      try {
        tab.close();
      } catch (e) {
        /* the tab may already be gone */
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      viewContext.notification.show(
        await readBlobErrorMessage(err, "Could not open the report PDF"),
        "error",
        true
      );
    } finally {
      pdfInFlight.current = false;
      setPdfLoading(false);
    }
  }

  function setEditField(personKey, field, value) {
    setEditing((prev) => ({
      ...prev,
      [personKey]: { ...(prev[personKey] || {}), [field]: value },
    }));
  }

  async function saveContactDetails(personId, personKey) {
    if (!personId) return;
    setActionLoading((prev) => ({ ...prev, [personKey]: "save" }));
    try {
      await Axios.patch(
        `/api/ubo-report-people/${personId}/contact-details`,
        editing[personKey] || {}
      );
      viewContext.notification.show("Contact details saved", "success", true);
      setEditing((prev) => {
        const next = { ...prev };
        delete next[personKey];
        return next;
      });
      refresh();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Save failed",
        "error",
        true
      );
    } finally {
      setActionLoading((prev) => ({ ...prev, [personKey]: null }));
    }
  }

  async function triggerVerification(personId, personKey) {
    if (!personId) return;
    setActionLoading((prev) => ({ ...prev, [personKey]: "verify" }));
    try {
      const res = await Axios.post(
        `/api/ubo-report-people/${personId}/trigger-verification`,
        {}
      );
      viewContext.notification.show(
        `Verification triggered (${res.data.verification_id})`,
        "success",
        true
      );
      refresh();
    } catch (err) {
      const message = err.response?.data?.message || "Verification failed";
      if (err.response?.status === 409 && err.response?.data?.last_status) {
        const lastStatus = err.response.data.last_status;
        setConfirmDialog({
          title: "Start a new verification?",
          body: (
            <>
              <p>{message}</p>
              <p className="mt-2">
                The previous verification ended in <strong>{lastStatus}</strong>.
              </p>
            </>
          ),
          confirmText: "Re-verify",
          onConfirm: async () => {
            closeConfirm();
            try {
              const res2 = await Axios.post(
                `/api/ubo-report-people/${personId}/trigger-verification`,
                { allow_retrigger: true }
              );
              viewContext.notification.show(
                `Re-verification triggered (${res2.data.verification_id})`,
                "success",
                true
              );
              refresh();
            } catch (retryErr) {
              viewContext.notification.show(
                retryErr.response?.data?.message || "Re-verification failed",
                "error",
                true
              );
            }
          },
        });
      } else {
        viewContext.notification.show(message, "error", true);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, [personKey]: null }));
    }
  }

  async function syncPersonAddNew(personId, personKey, provider) {
    if (!personId) return;
    setActionLoading((prev) => ({ ...prev, [personKey]: `pms-${provider}-new` }));
    try {
      await Axios.post(`/api/ubo-report-people/${personId}/sync-pms`, {
        provider,
        action: "add_new",
      });
      viewContext.notification.show(`${provider.toUpperCase()} sync queued`, "success", true);
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || `${provider} sync failed`,
        "error",
        true
      );
    } finally {
      setActionLoading((prev) => ({ ...prev, [personKey]: null }));
    }
  }

  function openPersonMergeModal(personId, personKey) {
    setXpmModal({ mode: "person", personId, personKey });
  }

  async function submitPersonPmsAction({
    personId,
    personKey,
    target,
    action,
    loadingKey = "pms-xpm-link",
    successMessage = "XPM link recorded",
    errorMessage = "XPM link failed",
  }) {
    setActionLoading((prev) => ({ ...prev, [personKey]: loadingKey }));
    try {
      await Axios.post(`/api/ubo-report-people/${personId}/sync-pms`, {
        provider: "xpm",
        action,
        target_external_id: target.ContactId,
        target_kind: target.kind || "contact",
      });
      viewContext.notification.show(successMessage, "success", true);
      refresh();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || errorMessage,
        "error",
        true
      );
    } finally {
      setActionLoading((prev) => ({ ...prev, [personKey]: null }));
    }
  }

  async function onPersonMergeTargetSelected(target) {
    if (!xpmModal || xpmModal.mode !== "person") return;
    const { personId, personKey } = xpmModal;
    setXpmModal(null);
    if (target.kind === "client") {
      setPersonClientChoice({ personId, personKey, target });
      return;
    }
    await submitPersonPmsAction({
      personId,
      personKey,
      target,
      action: "merge_link",
      successMessage: "XPM contact link recorded",
      errorMessage: "XPM contact link failed",
    });
  }

  async function submitPersonClientChoice(action) {
    if (!personClientChoice) return;
    const { personId, personKey, target } = personClientChoice;
    setPersonClientChoice(null);
    await submitPersonPmsAction({
      personId,
      personKey,
      target,
      action,
      loadingKey:
        action === "create_contact_under_client"
          ? "pms-xpm-contact"
          : "pms-xpm-client-update",
      successMessage:
        action === "create_contact_under_client"
          ? "XPM contact creation queued"
          : "XPM client update queued",
      errorMessage:
        action === "create_contact_under_client"
          ? "XPM contact creation failed"
          : "XPM client update failed",
    });
  }

  async function openOrgAddNew() {
    // Cancel any in-progress merge UI to avoid double-display.
    setOrgMergeTarget(null);
    setOrgPreview(null);
    setOrgFieldPicks({});
    setOrgSyncLoading(true);
    try {
      const res = await Axios.get(
        `/api/ubo-reports/${params.report_id}/xpm/preview-fields`
      );
      const preview = res.data?.data || null;
      if (!preview) throw new Error("No field preview returned");
      setAddNewPreview(preview);
      // Pre-fill editable values from the resolved object.
      const initial = {};
      for (const k of preview.available_company_fields) {
        initial[k] = preview.resolved?.[k] || "";
      }
      setAddNewValues(initial);
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Could not load field preview",
        "error",
        true
      );
    } finally {
      setOrgSyncLoading(false);
    }
  }

  async function submitOrgAddNew() {
    if (!addNewValues.Name || addNewValues.Name.trim() === "") {
      viewContext.notification.show("Name is required", "error", true);
      return;
    }
    setOrgSyncLoading(true);
    try {
      await Axios.post(`/api/ubo-reports/${params.report_id}/sync-pms`, {
        provider: "xpm",
        action: "add_new",
        field_overrides: addNewValues,
        include_ubo_contacts: orgIncludeUbos,
      });
      viewContext.notification.show("Org XPM sync queued", "success", true);
      setAddNewPreview(null);
      setAddNewValues({});
      refresh();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Org XPM sync failed",
        "error",
        true
      );
    } finally {
      setOrgSyncLoading(false);
    }
  }

  function cancelOrgAddNew() {
    setAddNewPreview(null);
    setAddNewValues({});
  }

  function openOrgMergeModal() {
    setOrgMergeTarget(null);
    setOrgPreview(null);
    setOrgFieldPicks({});
    setXpmModal({ mode: "org" });
  }

  async function onOrgMergeTargetSelected(target) {
    if (target?.kind !== "client") {
      viewContext.notification.show("Select an XPM client for org merge", "error", true);
      return;
    }
    setXpmModal(null);
    setOrgMergeTarget(target);
    setOrgSyncLoading(true);
    try {
      const res = await Axios.get(
        `/api/ubo-reports/${params.report_id}/xpm/preview-fields`
      );
      const preview = res.data?.data || null;
      setOrgPreview(preview);
      const initialPicks = {};
      const initialEdits = {};
      for (const k of preview?.available_company_fields || []) {
        initialPicks[k] = false;
        initialEdits[k] = preview?.resolved?.[k] || "";
      }
      setOrgFieldPicks(initialPicks);
      setOrgFieldEdits(initialEdits);
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Could not load field preview",
        "error",
        true
      );
      setOrgMergeTarget(null);
    } finally {
      setOrgSyncLoading(false);
    }
  }

  async function submitOrgMerge() {
    if (!orgMergeTarget) return;
    const fieldsToUpdate = Object.keys(orgFieldPicks).filter((k) => orgFieldPicks[k]);
    if (fieldsToUpdate.length === 0) {
      viewContext.notification.show(
        "Tick at least one field to overwrite, or use 'Add new' instead.",
        "error",
        true
      );
      return;
    }
    // Send only the user-supplied values for the ticked fields. Server applies
    // them verbatim (no template re-resolution).
    const fieldOverrides = {};
    for (const k of fieldsToUpdate) {
      fieldOverrides[k] = orgFieldEdits[k] ?? "";
    }
    setOrgSyncLoading(true);
    try {
      await Axios.post(`/api/ubo-reports/${params.report_id}/sync-pms`, {
        provider: "xpm",
        action: "merge_update",
        target_external_id: orgMergeTarget.ContactId,
        target_kind: orgMergeTarget.kind || "client",
        fields_to_update: fieldsToUpdate,
        field_overrides: fieldOverrides,
      });
      viewContext.notification.show("Org XPM merge queued", "success", true);
      setOrgMergeTarget(null);
      setOrgPreview(null);
      setOrgFieldPicks({});
      setOrgFieldEdits({});
      refresh();
    } catch (err) {
      viewContext.notification.show(
        err.response?.data?.message || "Org XPM merge failed",
        "error",
        true
      );
    } finally {
      setOrgSyncLoading(false);
    }
  }

  return (
    <Grid>
      <Row>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold flex items-center gap-3">
            UBO Report
            <StatusBadge status={status} />
          </h1>
          {status === "ready" && data.pdf_available && (
            <Button
              text={pdfLoading ? "Opening…" : "Open PDF"}
              color="blue"
              small
              action={openPdf}
              loading={pdfLoading}
            />
          )}
        </div>
      </Row>

      <Card>
        <SectionTitle
          title={data.entity_name || organisation?.name || data.identifier}
          subtitle="Report summary"
        />
        <FieldGrid
          items={[
            { label: "Identifier type", value: formatIdentifierType(data.identifier_type) },
            { label: "Identifier", value: data.identifier || "N/A" },
            { label: "Provider", value: formatValue(data.provider) },
            { label: "Provider report ID", value: formatValue(data.provider_report_id) },
            { label: "Provider progress", value: formatValue(data.provider_progress) },
            { label: "Created", value: formatDate(data.created_at) },
            { label: "Updated", value: formatDate(data.updated_at) },
            { label: "Lookup entity", value: data.lookup_snapshot?.entity_name || "N/A" },
            { label: "Lookup status", value: data.lookup_snapshot?.status || "N/A" },
          ]}
        />
      </Card>

      {status === "pending" && (
        <Card>
          <div className="flex items-center gap-3">
            <Loader />
            <div>
              <div className="font-medium">Generating UBO report</div>
              <div className="text-sm text-slate-500">
                This can take up to a couple of minutes. You can leave this
                page and come back later.
              </div>
            </div>
          </div>
        </Card>
      )}

      {status === "failed" && (
        <Card>
          <div className="text-sg-red font-medium">Report failed</div>
          <div className="text-sm text-slate-500 mt-1">{data.error || "Unknown error"}</div>
        </Card>
      )}

      {status === "ready" && (
        <>
          <Card>
            <SectionTitle title="Organisation" subtitle="Registered entity details returned in the report." />
            <FieldGrid
              items={[
                { label: "Name", value: organisation?.name || data.entity_name || "N/A" },
                { label: "ASIC identifier", value: displayOrganisationIdentifier(organisation) },
                { label: "ABN", value: organisation?.abn || "N/A" },
                { label: "Status", value: organisation?.status || "N/A" },
                { label: "Type", value: organisation?.type || "N/A" },
                { label: "Class", value: organisation?.class || "N/A" },
                { label: "Subclass", value: organisation?.subClass || "N/A" },
                { label: "Registration date", value: formatDate(organisation?.registrationDate) },
                { label: "Locality", value: organisation?.locality || "N/A" },
              ]}
            />
          </Card>

          <Card>
            <SectionTitle title={`Organisation Addresses (${organisationAddresses.length})`} />
            <DataTable
              columns={[
                { label: "Type", render: (row) => formatValue(row.type) },
                { label: "Address", render: (row) => formatValue(row.fullAddress || displayAddress(row)) },
                { label: "Start date", render: (row) => formatDate(row.startDate) },
              ]}
              rows={organisationAddresses}
              emptyText="No organisation addresses returned."
            />
          </Card>

          <Card>
            <SectionTitle
              title="Sync to XPM"
              subtitle="Create a new XPM client for this organisation, or merge into an existing one. UBOs are added as contacts under the chosen client."
            />
            <div className="mb-4 flex flex-wrap gap-2">
              <Button
                text={orgSyncLoading ? "..." : "Add new in XPM"}
                color="blue"
                action={openOrgAddNew}
                disabled={orgSyncLoading || !!addNewPreview}
              />
              <Button
                text="Merge with existing"
                outline
                action={openOrgMergeModal}
                disabled={orgSyncLoading || !!addNewPreview}
              />
            </div>

            {addNewPreview && (
              <div className="mb-4 rounded border border-slate-200 p-4">
                <div className="mb-2 text-sm font-medium">
                  Review the values that will be sent to XPM
                </div>
                {addNewPreview.is_using_defaults && (
                  <div className="mb-3 rounded border border-sg-orange-bg bg-sg-orange-bg px-3 py-2 text-sm text-sg-orange">
                    No XPM field mapping is configured for this account. Default
                    mappings have been applied — edit any value below before submitting.
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {addNewPreview.available_company_fields.map((field) => (
                    <label key={field} className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">{field}</span>
                      <input
                        className="w-full rounded border px-3 py-2"
                        value={addNewValues[field] ?? ""}
                        onChange={(e) =>
                          setAddNewValues((prev) => ({ ...prev, [field]: e.target.value }))
                        }
                        placeholder={field === "Name" ? "Required" : ""}
                      />
                    </label>
                  ))}
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={orgIncludeUbos}
                    onChange={(e) => setOrgIncludeUbos(e.target.checked)}
                  />
                  Add UBOs as contacts under the new client
                </label>
                <div className="mt-3 flex gap-2">
                  <Button
                    text={orgSyncLoading ? "..." : "Create in XPM"}
                    color="blue"
                    action={submitOrgAddNew}
                    disabled={orgSyncLoading}
                  />
                  <Button text="Cancel" outline action={cancelOrgAddNew} />
                </div>
              </div>
            )}

            {orgMergeTarget && orgPreview && (
              <div className="mb-4 rounded border border-slate-200 p-4">
                <div className="mb-2 text-sm">
                  <span className="font-medium">Selected XPM client:</span>{" "}
                  {orgMergeTarget.Name}
                  {orgMergeTarget.EmailAddress ? ` · ${orgMergeTarget.EmailAddress}` : ""}
                </div>
                <div className="mb-3 text-sm text-slate-600">
                  Tick a field to overwrite, then edit the value to send. Only ticked
                  fields are sent. Merge updates the client only — no UBO contacts are added.
                </div>
                <div className="flex flex-col gap-3">
                  {orgPreview.available_company_fields.map((field) => {
                    const checked = !!orgFieldPicks[field];
                    return (
                      <div key={field} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-3"
                          checked={checked}
                          onChange={(e) =>
                            setOrgFieldPicks((prev) => ({ ...prev, [field]: e.target.checked }))
                          }
                        />
                        <label className="flex flex-1 flex-col gap-1 text-sm">
                          <span className="font-medium text-slate-700">{field}</span>
                          <input
                            className={`w-full rounded border px-3 py-2 ${
                              checked ? "" : "bg-slate-50 text-slate-500"
                            }`}
                            value={orgFieldEdits[field] ?? ""}
                            onChange={(e) =>
                              setOrgFieldEdits((prev) => ({ ...prev, [field]: e.target.value }))
                            }
                            placeholder={field}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    text={orgSyncLoading ? "..." : "Submit merge"}
                    color="blue"
                    action={submitOrgMerge}
                    disabled={orgSyncLoading}
                  />
                  <Button
                    text="Cancel"
                    outline
                    action={() => {
                      setOrgMergeTarget(null);
                      setOrgPreview(null);
                      setOrgFieldPicks({});
                    }}
                  />
                </div>
              </div>
            )}

            {orgPmsLinks.length > 0 && (
              <DataTable
                columns={[
                  { label: "Action", render: (row) => formatValue(row.action) },
                  { label: "External ID", render: (row) => formatValue(row.external_id || row.target_external_id) },
                  { label: "Fields updated", render: (row) => Array.isArray(row.fields_updated) ? row.fields_updated.join(", ") : "" },
                  { label: "Synced", render: (row) => formatDate(row.synced_at || row.queued_at) },
                ]}
                rows={orgPmsLinks}
                emptyText="No XPM sync activity yet."
              />
            )}
          </Card>

          <Card>
            <SectionTitle
              title={`Beneficial Owners (${people.length})`}
              subtitle="These are the people you can review, amend, and action from this page."
            />
            {!people.length ? (
              <div className="text-sm text-slate-500">
                No beneficial owners were returned by the provider for this entity.
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {people.map((person, index) => {
                  const personKey = person.id || person.entity_key || `person-${index}`;
                  const draft = editing[personKey] || {};
                  const verificationActive = !!person.active_verification_id;

                  return (
                    <div key={personKey} className="rounded border border-slate-200 p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <div className="text-base font-semibold text-slate-900">
                              {displayName(person)}
                            </div>
                            <div className="mt-1 text-sm text-slate-500">
                              {person.ownership_pct != null
                                ? `${person.ownership_pct}% ownership`
                                : "Ownership not provided"}
                              {person.active_verification_id
                                ? ` · Active verification ${person.active_verification_id}`
                                : ""}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              small
                              text={
                                actionLoading[personKey] === "verify"
                                  ? "..."
                                  : verificationActive
                                  ? "Verification sent"
                                  : "Trigger verification"
                              }
                              color={verificationActive ? "gray" : "blue"}
                              action={() => triggerVerification(person.id, personKey)}
                              disabled={!person.id || !!actionLoading[personKey]}
                            />
                            <Button
                              small
                              text={
                                actionLoading[personKey] === "pms-xpm-new"
                                  ? "..."
                                  : "XPM (new)"
                              }
                              color="green"
                              action={() => syncPersonAddNew(person.id, personKey, "xpm")}
                              disabled={!person.id || !!actionLoading[personKey]}
                            />
                            <Button
                              small
                              text={
                                [
                                  "pms-xpm-link",
                                  "pms-xpm-contact",
                                  "pms-xpm-client-update",
                                ].includes(actionLoading[personKey])
                                  ? "..."
                                  : "XPM (merge)"
                              }
                              outline
                              action={() => openPersonMergeModal(person.id, personKey)}
                              disabled={!person.id || !!actionLoading[personKey]}
                            />
                            <Button
                              small
                              text={
                                actionLoading[personKey] === "pms-karbon-new"
                                  ? "..."
                                  : "Karbon (new)"
                              }
                              color="green"
                              action={() => syncPersonAddNew(person.id, personKey, "karbon")}
                              disabled={!person.id || !!actionLoading[personKey]}
                            />
                          </div>
                        </div>

                        <FieldGrid
                          items={[
                            { label: "First name", value: person.first_name || "N/A" },
                            { label: "Middle name", value: person.middle_name || "N/A" },
                            { label: "Last name", value: person.last_name || "N/A" },
                            { label: "DOB", value: formatDate(person.dob) },
                            { label: "Nationality", value: person.nationality || "N/A" },
                            {
                              label: "Country of residence",
                              value: person.country_of_residence || "N/A",
                            },
                            {
                              label: "Nature of control",
                              value: person.nature_of_control || "N/A",
                            },
                            {
                              label: "Shares beneficially held",
                              value: formatValue(person.shares_beneficially_held),
                            },
                            { label: "Contact ID", value: person.contact_id || "N/A" },
                          ]}
                        />

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <label className="text-sm">
                            <div className="mb-1 font-medium text-slate-700">Email</div>
                            <input
                              className="w-full rounded border px-3 py-2"
                              placeholder="Email"
                              value={draft.email ?? person.email ?? ""}
                              onChange={(e) =>
                                setEditField(personKey, "email", e.target.value)
                              }
                            />
                          </label>
                          <label className="text-sm">
                            <div className="mb-1 font-medium text-slate-700">Phone</div>
                            <input
                              className="w-full rounded border px-3 py-2"
                              placeholder="Phone"
                              value={draft.phone ?? person.phone ?? ""}
                              onChange={(e) =>
                                setEditField(personKey, "phone", e.target.value)
                              }
                            />
                          </label>
                          <label className="text-sm">
                            <div className="mb-1 font-medium text-slate-700">Address</div>
                            <input
                              className="w-full rounded border px-3 py-2"
                              placeholder="Address"
                              value={draft.address ?? person.address ?? ""}
                              onChange={(e) =>
                                setEditField(personKey, "address", e.target.value)
                              }
                            />
                          </label>
                        </div>

                        {editing[personKey] ? (
                          <div className="flex gap-2">
                            <Button
                              small
                              text={
                                actionLoading[personKey] === "save" ? "Saving..." : "Save"
                              }
                              color="blue"
                              action={() => saveContactDetails(person.id, personKey)}
                              disabled={!person.id || !!actionLoading[personKey]}
                            />
                            <Button
                              small
                              text="Cancel"
                              outline
                              action={() =>
                                setEditing((prev) => {
                                  const next = { ...prev };
                                  delete next[personKey];
                                  return next;
                                })
                              }
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle title={`Officeholders (${officeholders.length})`} />
            <DataTable
              columns={[
                { label: "Type", render: (row) => formatValue(row.type) },
                { label: "Name", render: (row) => displayName(row) },
                { label: "DOB", render: (row) => formatDate(row.dateOfBirth) },
                { label: "Start date", render: (row) => formatDate(row.startDate) },
                { label: "Address", render: (row) => displayAddress(row) },
              ]}
              rows={officeholders}
              emptyText="No officeholders returned."
            />
          </Card>

          <Card>
            <SectionTitle title={`Shareholders (${shareholders.length})`} />
            <DataTable
              columns={[
                { label: "Name", render: (row) => displayName(row) },
                { label: "Holder type", render: (row) => formatValue(row.type) },
                { label: "Document", render: (row) => formatValue(row.documentNumber) },
                { label: "Share class", render: (row) => formatValue(row.share?.type) },
                { label: "Amount", render: (row) => formatNumber(row.share?.amount) },
                {
                  label: "Beneficially owned",
                  render: (row) => formatValue(row.share?.beneficiallyOwned),
                },
                { label: "Joint holding", render: (row) => formatValue(row.share?.jointHolding) },
                {
                  label: "Organisation number",
                  render: (row) => formatValue(row.asicOrganisationNumber),
                },
                { label: "Address", render: (row) => displayAddress(row) },
              ]}
              rows={shareholders}
              emptyText="No shareholders returned."
            />
          </Card>

          <Card>
            <SectionTitle title={`Share Capital (${shareCapitalGroup.length})`} />
            <DataTable
              columns={[
                { label: "Class code", render: (row) => formatValue(row.classCode) },
                { label: "Class title", render: (row) => formatValue(row.classTitle) },
                { label: "Shares", render: (row) => formatNumber(row.numberOfShares) },
                { label: "Paid", render: (row) => formatNumber(row.totalAmountPaid) },
                {
                  label: "Due and payable",
                  render: (row) => formatNumber(row.totalAmountDueAndPayable),
                },
              ]}
              rows={shareCapitalGroup}
              emptyText="No share capital returned."
            />
          </Card>

          <Card>
            <SectionTitle title={`Linked Companies (${linkedCompanies.length})`} />
            <DataTable
              columns={[
                { label: "Name", render: (row) => formatValue(row.name) },
                { label: "Ownership", render: (row) => formatValue(row.ownership) },
                { label: "Address", render: (row) => formatValue(row.fullAddress || displayAddress(row)) },
              ]}
              rows={linkedCompanies}
              emptyText="No linked companies returned."
            />
          </Card>

          <Card>
            <SectionTitle title={`International Companies (${internationalCompanies.length})`} />
            <DataTable
              columns={[
                { label: "Name", render: (row) => formatValue(row.name) },
                { label: "Ownership", render: (row) => formatValue(row.ownership) },
                { label: "Address", render: (row) => formatValue(row.fullAddress || displayAddress(row)) },
              ]}
              rows={internationalCompanies}
              emptyText="No international companies returned."
            />
          </Card>

          <div className="flex flex-col gap-4">
            <SectionTitle
              title={`Investigated Companies (${investigatedCompanies.length})`}
              subtitle="These related entities are included in the CreditorWatch ownership chain."
            />
            {!investigatedCompanies.length ? (
              <Card>
                <div className="text-sm text-slate-500">
                  No investigated companies were returned.
                </div>
              </Card>
            ) : (
              investigatedCompanies.map((company, index) => (
                <CompanyCard
                  key={company.asicOrganisationNumber || company.name || index}
                  company={company}
                  titlePrefix={`Investigated company ${index + 1}`}
                />
              ))
            )}
          </div>
        </>
      )}

      <XpmSearchModal
        open={!!xpmModal}
        title={
          xpmModal?.mode === "org"
            ? "Find an existing XPM client to merge into"
            : "Search XPM clients or contacts"
        }
        onClose={() => setXpmModal(null)}
        onSelect={(target) => {
          if (xpmModal?.mode === "org") onOrgMergeTargetSelected(target);
          else onPersonMergeTargetSelected(target);
        }}
        restrictToKind={xpmModal?.mode === "org" ? "client" : null}
      />

      {personClientChoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
          <div className="w-full max-w-md rounded bg-white p-6 shadow-menu">
            <h2 className="mb-2 text-lg font-semibold text-slate-900">
              Choose XPM merge action
            </h2>
            <div className="mb-4 text-sm text-slate-700">
              <p>
                Selected XPM client:{" "}
                <strong>{personClientChoice.target.Name || "(unnamed)"}</strong>
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                small
                outline
                text="Cancel"
                action={() => setPersonClientChoice(null)}
              />
              <Button
                small
                color="blue"
                text="Add as contact"
                action={() => submitPersonClientChoice("create_contact_under_client")}
              />
              <Button
                small
                color="blue"
                text="Update client"
                action={() => submitPersonClientChoice("merge_update_client")}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.title}
        body={confirmDialog?.body}
        confirmText={confirmDialog?.confirmText}
        cancelText={confirmDialog?.cancelText}
        destructive={confirmDialog?.destructive}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={closeConfirm}
      />
    </Grid>
  );
}

export default EntityOnboardingReportView;
